import { CircleCircle, WorkflowWorkflowType } from '@calm/evexia-web-sdk';
import createWorkflowApi from '@rhg/libs/api/create-workflow-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    invalidateQueryKey,
    queryKeys,
    useMutation,
} from '@rhg/libs/react-query';
import {
    workflowByDefnIdForRecipientQueryOptions,
    workflowByTypeForRecipientQueryOptions,
} from '../use-workflows';

interface WorkflowNextStepParameters {
    circle: CircleCircle;
    workflowType?: WorkflowWorkflowType;
    workflowDefnId?: string;
}

export const workflowNextStepQueryOptions = {
    queryKey: function ({
        workflowDefnId,
        recipientId,
    }: {
        workflowDefnId: string;
        recipientId: number;
    }) {
        return [
            queryKeys.workflowNextStepByDefnId,
            recipientId,
            workflowDefnId,
        ];
    },
    queryFn: async function ({
        getAccessTokenSilently,
        circleAuthorizationToken,
        recipientId,
        workflowDefnId,
    }: {
        getAccessTokenSilently: () => Promise<string>;
        circleAuthorizationToken: string;
        recipientId: number;
        workflowDefnId: string;
    }) {
        const workflowApi = createWorkflowApi(getAccessTokenSilently);

        const response = await workflowApi.v1GetNextStep({
            workflowDefnId,
            circleAuthorizationToken,
            recipientId,
        });

        return response.data;
    },
    queryOptions: function ({
        workflowDefnId,
        recipientId,
        circleAuthorizationToken,
        isAuthenticated,
    }: {
        workflowDefnId: string;
        recipientId: number;
        circleAuthorizationToken: string;
        isAuthenticated: boolean;
    }) {
        return {
            enabled: !!(
                isAuthenticated &&
                workflowDefnId &&
                recipientId &&
                circleAuthorizationToken
            ),
        };
    },
};

export function useFetchWorkflowNextStep() {
    const { getAccessTokenSilently } = useAuth();
    const { queryKey, queryFn } = workflowNextStepQueryOptions;
    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async ({
            workflowDefnId,
            circle,
        }: WorkflowNextStepParameters) => {
            return queryFn({
                getAccessTokenSilently,
                workflowDefnId: workflowDefnId!,
                recipientId: circle.recipient.id,
                circleAuthorizationToken: circle?.authorizationJWT ?? '',
            });
        },
        onSuccess: (response, { circle, workflowDefnId, workflowType }) => {
            if (response) {
                invalidateQueryKey(
                    workflowByTypeForRecipientQueryOptions.queryKey({
                        workflowType,
                        recipientId: circle.recipient.id,
                    })
                );
                invalidateQueryKey(
                    workflowByDefnIdForRecipientQueryOptions.queryKey({
                        workflowDefnId,
                        recipientId: circle.recipient.id,
                    })
                );
                invalidateQueryKey(
                    queryKey({
                        workflowDefnId: workflowDefnId!,
                        recipientId: circle.recipient.id,
                    })
                );
            }
        },
    });
    return {
        fetchWorkflowNextStep: mutate,
        fetchWorkflowNextStepAsync: mutateAsync,
        ...otherProps,
    };
}
